import { useCallback, useEffect, useState } from "react";

export type Appearance = "light" | "dark" | "system";

const prefersDark = () => {
  if (typeof window === "undefined") {
    return false;
  }

  return window.matchMedia("(prefers-color-scheme: dark)").matches;
};

const setCookie = async (name: string, value: string, days = 365) => {
  if (typeof document === "undefined") {
    return;
  }

  // biome-ignore lint/style/noMagicNumbers: Cookie expiration calculation.
  const expiresMs = Date.now() + days * 24 * 60 * 60 * 1000;

  if (typeof cookieStore !== "undefined" && cookieStore.set) {
    await cookieStore.set({
      name,
      value,
      path: "/",
      sameSite: "lax",
      expires: expiresMs,
    });
  } else {
    const expires = new Date(expiresMs);
    // biome-ignore lint/suspicious/noDocumentCookie: Fallback to document.cookie.
    document.cookie = `${name}=${value};path=/;expires=${expires.toUTCString()};sameSite=lax`;
  }
};

const applyTheme = (appearance: Appearance) => {
  const isDark =
    appearance === "dark" || (appearance === "system" && prefersDark());

  document.documentElement.classList.toggle("dark", isDark);
  document.documentElement.style.colorScheme = isDark ? "dark" : "light";
};

const mediaQuery = () => {
  if (typeof window === "undefined") {
    return null;
  }

  return window.matchMedia("(prefers-color-scheme: dark)");
};

const handleSystemThemeChange = () => {
  const currentAppearance = localStorage.getItem("appearance") as Appearance;
  applyTheme(currentAppearance || "light");
};

export function initializeTheme() {
  const savedAppearance =
    (localStorage.getItem("appearance") as Appearance) || "light";

  applyTheme(savedAppearance);

  // Add the event listener for system theme changes...
  mediaQuery()?.addEventListener("change", handleSystemThemeChange);
}

export function useAppearance() {
  const [appearance, setAppearance] = useState<Appearance>("light");

  const updateAppearance = useCallback((_mode: Appearance) => {
    // For now we always set the appearance to light - https://github.com/lavendla/curera/pull/230
    setAppearance("light");
    localStorage.setItem("appearance", "light");
    setCookie("appearance", "light");
    applyTheme("light");

    // setAppearance(mode);

    // // Store in localStorage for client-side persistence...
    // localStorage.setItem("appearance", mode);

    // // Store in cookie for SSR...
    // setCookie("appearance", mode);

    // applyTheme(mode);
  }, []);

  useEffect(() => {
    const savedAppearance = localStorage.getItem(
      "appearance"
    ) as Appearance | null;
    updateAppearance(savedAppearance || "light");

    return () =>
      mediaQuery()?.removeEventListener("change", handleSystemThemeChange);
  }, [updateAppearance]);

  return { appearance, updateAppearance, prefersDark } as const;
}
